import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {CpConfigCheckService} from '../../../service/api/cp-config-check.service';
import {
    ProvisionConfigCheckEntryDto,
    ProvisionConfigCheckResultDto
} from '../../../model/dto/api/provision-config-check-result.dto';
import {ProvisionMessageSeverity} from '../../../enum/provision-message-severity.enum';
import {CpFeatureRight} from '../../../service/domain/cp-feature-right';

/** Filter state of the check report list. */
type CpConfigCheckFilter = 'all' | 'errors' | 'warnings' | 'infos';

/**
 * Check report panel of the provision configuration — navItem `configCheck` of the ProvisionType
 * object page (`cp-provision-type-base.component.ts`, 04-frontend.md §6). It is NOT a top-level
 * nav item of the cp config page.
 *
 * Built 1:1 following the documented repo original (PAT §I.1 "on-demand config check endpoint", cw/ns
 * construction): a live report WITHOUT persistence. Built concretely like
 * `features/feature-core/ns/page/ns-notification-type-poc/components/config-check/
 * ns-poc-config-check.component.*` — header row with error/warning counters, filter bar,
 * entry list with a severity border on the left, empty state "Alle Prüfungen bestanden".
 *
 * This component calls `CpConfigCheckService.checkProvisionType()`
 * (`GET /mvsa/cp/configCheck/provisionType/{id}`) itself and renders the flat entry list,
 * ordered by code, CR-001…CR-032 (03-architektur-backend.md §12.1):
 *
 *   CR-001/002 ERROR  gaps or overlaps of the ProvisionAgentGroup periods
 *   CR-003     ERROR  missing event mapping (K-13) for an engine event 0–5
 *   CR-004     ERROR  missing payout booking type on the ProvisionType
 *   CR-005     ERROR/WARN  default relevance row missing or does not cover the period
 *   CR-006     WARN   all-covering ELIGIBLE default row (deliberate allowance)
 *   CR-007     ERROR  logic consistency (logic ⇔ import/export fields)
 *   CR-008/009 WARN   matrix without rows or assignment to an invalid matrix
 *   CR-010/011 WARN   multiple main agents, priority duplicates
 *   CR-012     ERROR  result consistency of the assignments
 *   CR-013/014 WARN   run period gaps, event mappings without a reader
 *   CR-015     INFO   agent without a balance row
 *   CR-016     ERROR  provision type without an active contract event — it never books
 *   CR-017     ERROR  CONTRACT_PRICE_CHANGE event without a K-16 price-reason row — never fires
 *   CR-018     WARN   K-16 row on a CONTRACT_START event — nobody reads it
 *   CR-019     ERROR  more than one active CONTRACT_START row per provision type
 *   CR-020     ERROR  the same price reason in two event rows of the same type
 *   CR-021     ERROR  active component without a K-10 assignment row — it never books
 *   CR-022     WARN   component role without stock: no cm_contract_agent row with that assignment
 *   CR-023     ERROR  orderPriority duplicates among the components of one type (cap determinism)
 *   CR-024     WARN   K-10 liabilityTypeOverwrite points at an inactive ProvisionLiabilityType
 *   CR-025     INFO   K-10 overwrite deviates from the K-07 result of the same constellation
 *   CR-026     WARN   assignment uses former-contract dimensions, but no contract carries one
 *   CR-027     WARN   contract with formerContractIdentifier but without the formerContract FK
 *   CR-028     WARN   component period does not cover the range from goLiveDate on
 *   CR-029     WARN   ProvisionContractAgent with source = MANUAL without an active booking
 *   CR-030     WARN   eligible ProvisionContract, agentsResolved = true, yet not a single rate row
 *   CR-031     ERROR  zero-period ProvisionLiabilityType under a foreign alias, or a K-10
 *                     overwrite onto a liability type that does not exist
 *   CR-032     WARN   provisionAmountOverwriteThreshold unset although overwrites exist (AX-09)
 *   CR-033     WARN   event row frozen for calculated agents that the CURRENT configuration would
 *                     give one — the ONE ACTIONABLE rule of the catalog (FD-3/FD-7, see below)
 *
 * CR-033 MAKES THIS PANEL A WORKLIST, not just a report. Every other rule ends in "jump to the
 * object"; CR-033 ends in an ACT: each of its findings names one `cp.ProvisionContract` event row,
 * and the row carries the entry point to the guided action "Berechnete Vermittler neu ermitteln"
 * ({@link CpProvisionCalculatedAgentResetDialogComponent}). That is the mass-processing path of the
 * feature — deliberately one guided, previewed, per-row reset instead of a bulk endpoint, so guard
 * and preview are evaluated per row (§3.6 a).
 *
 * The action is offered but never performed here: this component only opens the dialog with the
 * row's id. The dialog loads the preview, requires the mandatory reason and is the only place that
 * writes. After a completed reset the report is re-run, because the finding is defined by the very
 * flag the reset clears and must therefore disappear.
 *
 * PERMISSION for that one action: um right `cp_agentChange` (AX-08, no new right), following the
 * module-wide optimistic pattern documented in {@link CpFeatureRight} — the button starts live, a
 * server 403 from inside the dialog demotes {@link canAgentChange} stickily and the button is then
 * disabled with the German reason in its tooltip. Nothing else in this panel is permission-gated;
 * it stays a read-only report everywhere else.
 *
 * "Jump to the affected object": every entry with `referenceEntityType` +
 * `referenceEntityId` opens the object via the standard object navigation
 * (`MvsObjectNavigationService.navigateTo(entry, 'main')`) — the same path as in
 * `NsConfigCheckResultComponent`. The referenced aliases are cp aliases and therefore
 * registered in `cp.entity-provider.ts`; without registration the jump would go nowhere (DoD).
 *
 * NO forms: the panel is pure display. Severity is read tolerantly — the backend value
 * can arrive either as an enum name ("ERROR") or as an ordinal (ProvisionMessageSeverity: INFO 0,
 * WARNING 1, ERROR 2, 02-datenmodell.md §2.1).
 */
@Component({
    selector: 'cp-config-check-panel',
    templateUrl: './cp-config-check-panel.component.html',
    styleUrls: ['./cp-config-check-panel.component.scss'],
    standalone: false
})
export class CpConfigCheckPanelComponent implements OnInit, OnChanges, OnDestroy {

    /** ProvisionType to check. `null`/`undefined` renders the "please select a type" empty state. */
    @Input() provisionTypeId: number;

    /** Emitted whenever a report has been loaded, so a host can react (e.g. badge counts). */
    @Output() onResultLoaded = new EventEmitter<ProvisionConfigCheckResultDto>();

    busy: boolean = false;
    initialized: boolean = false;

    /** Last loaded report, `null` while nothing has been requested yet. */
    result: ProvisionConfigCheckResultDto = null;

    /** German error text if the endpoint failed (e.g. not yet implemented on the backend). */
    loadError: string = null;

    activeFilter: CpConfigCheckFilter = 'all';

    /** Pre-computed list for the template — never filter inside the template. */
    filteredEntries: ProvisionConfigCheckEntryDto[] = [];

    /**
     * Options of the severity segment filter (`p-selectButton`), recomputed together with
     * `filteredEntries` so the counters follow the loaded report. Held as a field rather than a
     * getter: a getter would hand PrimeNG a new array on every change-detection pass.
     */
    filterOptions: { label: string, value: CpConfigCheckFilter }[] = [];

    /**
     * Visibility of the calculated-agent reset dialog, opened from a CR-033 finding
     * (`[(visible)]` on `cp-provision-calculated-agent-reset-dialog`).
     */
    resetDialogVisible: boolean = false;

    /** The `cp.ProvisionContract` event row the open reset dialog applies to. */
    resetProvisionContractId: number = null;

    /** Display label of that event row, taken from the finding's reference name. */
    resetProvisionContractLabel: string = null;

    /**
     * um right `cp_agentChange` for the CR-033 action — optimistic default, demoted once the
     * server answers a 403 from inside the dialog (module pattern, {@link CpFeatureRight}).
     */
    canAgentChange: boolean = true;

    /** Severity ordinals, exposed so the template can compare without magic numbers. */
    protected readonly cSeverityInfo = ProvisionMessageSeverity.INFO;
    protected readonly cSeverityWarning = ProvisionMessageSeverity.WARNING;
    protected readonly cSeverityError = ProvisionMessageSeverity.ERROR;

    private loadSubscription: Subscription;

    constructor(
        protected configCheckService: CpConfigCheckService,
        protected navigationService: MvsObjectNavigationService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId']) {
            this.initComponent();
        }
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
    }

    /**
     * Loads the report for the currently bound ProvisionType (or clears it when none is bound).
     */
    initComponent(): void {
        this.initialized = true;
        this.refresh();
    }

    /**
     * (Re-)runs the configuration check. Bound to the refresh button and to every
     * ProvisionType change.
     */
    refresh(): void {
        this.loadSubscription?.unsubscribe();
        this.loadError = null;

        if (!this.provisionTypeId) {
            this.result = null;
            this.filteredEntries = [];
            this.filterOptions = [];
            this.busy = false;
            return;
        }

        this.busy = true;
        this.loadSubscription = this.configCheckService.checkProvisionType(this.provisionTypeId).subscribe({
            next: (result: ProvisionConfigCheckResultDto) => {
                this.result = result;
                this.recomputeFilteredEntries();
                this.busy = false;
                this.onResultLoaded.emit(result);
            },
            error: () => {
                this.result = null;
                this.filteredEntries = [];
                this.filterOptions = [];
                this.loadError = 'Die Konfigurationsprüfung konnte nicht geladen werden.';
                this.busy = false;
            }
        });
    }

    setFilter(filter: CpConfigCheckFilter): void {
        this.activeFilter = filter;
        this.recomputeFilteredEntries();
    }

    /**
     * Opens the entity a finding points at, using the standard object navigation
     * (ns `NsConfigCheckResultComponent` pattern). Entries without a reference are inert.
     */
    openReference(entry: ProvisionConfigCheckEntryDto): void {
        if (!entry?.referenceEntityType || !entry?.referenceEntityId) {
            return;
        }
        const navigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            entry.referenceEntityType,
            entry.referenceEntityId,
            'object', '', '', '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(navigationEntry, 'main');
    }

    // ──────────────────────────────────────────────────────────────────────────────────────────
    // CR-033 — the one actionable finding: release an event row for a fresh calculated-agent
    // derivation (FD-7). Everything below only OFFERS the act; the dialog performs it.
    // ──────────────────────────────────────────────────────────────────────────────────────────

    /**
     * Whether this finding carries the reset action.
     *
     * Three conditions, all required: it is a CR-033 finding, it references an event row, and that
     * reference really is a `cp.ProvisionContract`. The last two matter because CR-033 also emits
     * an AGGREGATE finding once its worklist limit is exceeded ("Insgesamt N Ereigniszeilen …"),
     * and that one carries no reference at all — offering a reset on it would have nothing to reset.
     */
    isResettableEntry(entry: ProvisionConfigCheckEntryDto): boolean {
        return entry?.checkCode === 'CR-033'
            && entry?.referenceEntityType === 'cp.ProvisionContract'
            && !!entry?.referenceEntityId;
    }

    /** Opens the guided reset for the event row this finding names. */
    openResetDialog(entry: ProvisionConfigCheckEntryDto): void {

        if (!this.isResettableEntry(entry)) {
            return;
        }

        this.resetProvisionContractId = entry.referenceEntityId;
        this.resetProvisionContractLabel =
            entry.referenceEntityName ?? ('Ereigniszeile #' + entry.referenceEntityId);
        this.resetDialogVisible = true;
    }

    /**
     * A completed reset re-runs the check.
     *
     * The finding is defined by exactly the flag the reset clears
     * (`calculated_agents_resolved = TRUE`), so after a release the row must vanish from the
     * worklist. Leaving the stale list standing would invite a second reset on a row that is
     * already released — harmless (the act is idempotent) but confusing, and it would make the
     * operator lose their place in the list.
     */
    handleResetDone(): void {
        this.resetDialogVisible = false;
        this.refresh();
    }

    /**
     * The server refused `cp_agentChange` with a 403 — demote the mirrored flag so the panel stops
     * offering an action the server refuses. Sticky for this component instance, per the module
     * pattern; only a 403 reaches here, the dialog filters everything else out.
     */
    handleResetForbidden(): void {
        this.canAgentChange = false;
    }

    /** German reason shown on the disabled CR-033 button after a demotion. */
    get resetDeniedMessage(): string {
        return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
    }

    /** Severity of one entry as an ordinal, tolerant to name-or-ordinal serialization. */
    getSeverity(entry: ProvisionConfigCheckEntryDto): ProvisionMessageSeverity {
        return this.normalizeSeverity(entry?.severity);
    }

    /** German severity label — „Fehler" / „Warnung" / „Hinweis". */
    getSeverityLabel(entry: ProvisionConfigCheckEntryDto): string {
        switch (this.getSeverity(entry)) {
            case ProvisionMessageSeverity.ERROR:
                return 'Fehler';
            case ProvisionMessageSeverity.WARNING:
                return 'Warnung';
            default:
                return 'Hinweis';
        }
    }

    /**
     * Severity of one entry translated into a PrimeNG `p-tag` severity
     * (`'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast'`).
     *
     * The severity chip is a `<p-tag>` — the same component the bd reference uses for status chips
     * (`features/feature-bm/bd/component/shared/bd-run-history-tab/bd-run-history-tab.component.html`).
     * `TagModule` is reachable from this module: CpModule imports CoreModule, CoreModule exports
     * SharedModule, SharedModule exports NgPrimeUltimaModule and that module exports TagModule.
     */
    getSeverityTagSeverity(entry: ProvisionConfigCheckEntryDto): 'info' | 'warn' | 'danger' {
        switch (this.getSeverity(entry)) {
            case ProvisionMessageSeverity.ERROR:
                return 'danger';
            case ProvisionMessageSeverity.WARNING:
                return 'warn';
            default:
                return 'info';
        }
    }

    /** FontAwesome icon + colour token per severity (ns config-check pattern). */
    getSeverityIcon(entry: ProvisionConfigCheckEntryDto): string {
        switch (this.getSeverity(entry)) {
            case ProvisionMessageSeverity.ERROR:
                return 'fa-solid fa-circle-xmark text-red-500';
            case ProvisionMessageSeverity.WARNING:
                return 'fa-solid fa-triangle-exclamation text-yellow-500';
            default:
                return 'fa-solid fa-circle-info text-blue-500';
        }
    }

    /** Number of INFO findings — the result DTO only counts errors and warnings. */
    getInfoCount(): number {
        if (!this.result?.entries?.length) {
            return 0;
        }
        return this.result.entries.filter(
            entry => this.normalizeSeverity(entry.severity) === ProvisionMessageSeverity.INFO).length;
    }

    private recomputeFilteredEntries(): void {
        const entries = this.result?.entries ?? [];

        this.filterOptions = [
            {label: 'Alle (' + entries.length + ')', value: 'all'},
            {label: 'Fehler (' + (this.result?.totalErrors ?? 0) + ')', value: 'errors'},
            {label: 'Warnungen (' + (this.result?.totalWarnings ?? 0) + ')', value: 'warnings'},
            {label: 'Hinweise (' + this.getInfoCount() + ')', value: 'infos'}
        ];

        if (this.activeFilter === 'errors') {
            this.filteredEntries = entries.filter(
                entry => this.normalizeSeverity(entry.severity) === ProvisionMessageSeverity.ERROR);
        } else if (this.activeFilter === 'warnings') {
            this.filteredEntries = entries.filter(
                entry => this.normalizeSeverity(entry.severity) === ProvisionMessageSeverity.WARNING);
        } else if (this.activeFilter === 'infos') {
            this.filteredEntries = entries.filter(
                entry => this.normalizeSeverity(entry.severity) === ProvisionMessageSeverity.INFO);
        } else {
            this.filteredEntries = entries;
        }
    }

    /**
     * The backend may serialize an AlphaBaseEnum either as its ordinal or as its name; both are
     * accepted here so the panel does not depend on the serializer configuration.
     */
    private normalizeSeverity(severity: string | number): ProvisionMessageSeverity {
        if (severity === null || severity === undefined) {
            return ProvisionMessageSeverity.INFO;
        }
        if (typeof severity === 'number') {
            return severity as ProvisionMessageSeverity;
        }
        switch (severity.toString().toUpperCase()) {
            case 'ERROR':
                return ProvisionMessageSeverity.ERROR;
            case 'WARN':
            case 'WARNING':
                return ProvisionMessageSeverity.WARNING;
            default:
                return ProvisionMessageSeverity.INFO;
        }
    }

}
